import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { useContext } from "react";
import { useCreateSnapshot } from "../api/snapshots";
import { StrategyContext } from "../contexts/StrategyContext";

interface SnapshotCaptureDialogProps {
  open: boolean;
  imageData: string | null;
  onClose: () => void;
}

export default function SnapshotCaptureDialog({ open, imageData, onClose }: SnapshotCaptureDialogProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const { mutateAsync: createSnapshot, isPending } = useCreateSnapshot(projectId);
  const { selectedStrategy } = useContext(StrategyContext);

  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setDescription("");
      setError(null);
    }
  }, [open]);

  if (!open || !imageData) {
    return null;
  }

  const handleSave = async () => {
    if (!projectId || !imageData) return;
    try {
      await createSnapshot({
        description,
        image_data: imageData,
        mitigation_strategy_id: selectedStrategy?.id ?? null,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to save snapshot");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save snapshot</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" sx={{ mb: 1 }}>
          Provide a short description so the report-writing agent knows why this screenshot matters.
        </Typography>
        <TextField
          label="Description"
          placeholder="e.g. Fire fronts threatening the northern assets"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            maxHeight: 360,
          }}
        >
          <Box
            component="img"
            src={imageData}
            alt="Snapshot preview"
            sx={{ width: "100%", display: "block", objectFit: "cover" }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isPending}>
          {isPending ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Save snapshot"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
